import { Container, Box, Typography, Divider, Grid, Paper } from '@mui/material';
import {Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot} from '@mui/lab';
import {timelineItemClasses} from '@mui/lab/TimelineItem';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import SectionTitle from '../components/SectionTitle';
import LinkIcon from '@mui/icons-material/Link';
import schoolData from '../data/schoolDetails.json';

function Education() {
   const theme = useTheme();

   const schoolDetails = schoolData;

   function schoolDescription({ logo, name, degree, startYear, endYear, activities, awards, src }) {
      return (
         <TimelineItem>
            <TimelineSeparator>
               <Box component="img" src={logo} onClick={() => window.open(src, '_blank')} sx={{ 
                  width: { xs: 40, sm: 80, md: 120 }, 
                  height: { xs: 40, sm: 80, md: 120 },
                  borderRadius: '50%',
                  m: 1,
                  ':hover': {
                     transform: 'scale(1.1)',
                     transition: 'transform 0.2s',
                     cursor: 'pointer',
                  },
               }} />
               <TimelineConnector sx={{ my: 1, height: 'auto' }} />
            </TimelineSeparator>
            <TimelineContent
               sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 0,
                  px: 2,
                  my: 2,
               }}
            >
               <Paper
                  elevation={1}
                  sx={{
                     p: 3,
                     pb: 5,
                     width: '100%',
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                     <Typography variant="h2" sx={{ color: theme.palette.text.primary, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' }, mb: 1 }}>
                        {name}
                     </Typography>
                     <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                     }}>
                        <Typography variant="h4" sx={{ color: theme.palette.text.secondary, fontSize:{ xs: '1rem', sm: '1.2rem', md: '1.5rem' }, ml: 2}}>
                           {degree}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}>
                           {startYear} - {endYear}
                        </Typography>
                     </Box>   
                  </Box>
                  <Typography variant="h3" sx={{ color: theme.palette.text.primary, ml: 3, mt: 4, fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }}}>
                     Activities:
                  </Typography>
                  <Divider sx={{ backgroundColor: theme.palette.text.primary, marginBottom: 1 }} />
                  <Grid container spacing={2} sx={{ flexGrow: 1}}>
                     <Grid item sx={{ flexGrow: 1, py: 2}}>
                        {activities.map((activity, index) => (
                           <Box key={index} sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              my: 2,
                           }}>
                              <Box sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 flexGrow: 1,
                                 gap: 1,
                              }}>
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component='img' src={activity.logo} sx={{
                                       width: { xs: 40, sm: 60, md: 80 },
                                       height: { xs: 40, sm: 60, md: 80 },
                                       objectFit: 'contain',
                                       borderRadius: '4px',
                                    }} />
                                    <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' } }}>
                                             {activity.name}
                                          </Typography>
                                          {activity.link && <LinkIcon onClick={() => window.open(activity.link, '_blank')}sx={{ 
                                             color: theme.palette.text.primary,
                                             cursor: 'pointer',
                                             ':hover': {
                                                transform: 'scale(1.1)',
                                                transition: 'transform 0.2s',
                                             }
                                          }}/>}
                                       </Box>
                                       <Typography variant="caption" sx={{fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }, color: theme.palette.text.secondary }}>
                                          { activity.status }
                                       </Typography>
                                    </Box>
                                 </Box>
                                 <Box sx={{ position: 'relative', width: '100%' }}>
                                    {activity.images && (
                                       <Box
                                          component="img"
                                          src={activity.images}
                                          alt={activity.name}
                                          sx={{
                                             width: { xs: 50, sm: 200, md: 300 },
                                             height: 'auto',
                                             objectFit: 'contain',
                                             borderRadius: '4px',
                                             float: 'right',
                                             ml: 2,
                                             mb: 1,
                                             maxHeight: 300,
                                          }}
                                       />
                                    )}
                                    <Typography
                                       variant="body1"
                                       sx={{
                                          color: theme.palette.text.secondary,
                                          wordBreak: 'break-word',
                                          textAlign: 'justify',
                                          overflow: 'hidden',
                                          fontSize: { xs: '0.875rem', sm: '0.925rem', md: '1rem' },
                                       }}
                                       component="span"
                                       dangerouslySetInnerHTML={{
                                          __html: activity.description
                                          .replace(/\n/g, '<br />')
                                          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;'),
                                       }}
                                    />
                                 </Box>
                              </Box>
                           </Box>
                        ))}
                     </Grid>
                  </Grid>
                  <Typography variant="h3" sx={{ color: theme.palette.text.primary, ml: 3, fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' }, mt: 4 }}>
                     Awards:
                  </Typography>
                  <Divider sx={{ backgroundColor: theme.palette.text.primary, marginBottom: 1 }} />
                  <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                     <Grid item sx={12}>
                        {awards.length > 0 ? awards.map((award, index) => (
                           <Box key={index} sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              my: 2,
                           }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '0.875rem'}}>
                                 <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' } }}>
                                    {award.name}
                                 </Typography>
                                 {award.link && <LinkIcon onClick={() => window.open(award.link, '_blank')}sx={{ 
                                    color: theme.palette.text.primary,
                                    cursor: 'pointer',
                                    ':hover': {
                                       transform: 'scale(1.1)',
                                       transition: 'transform 0.2s',
                                    }
                                 }}/>}
                              </Box>
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                 {award.images && (
                                    <Box
                                       component="img"
                                       src={award.images}
                                       alt={award.name}
                                       sx={{
                                          width: { xs: 50, sm: 200, md: 300 },
                                          height: 'auto',
                                          objectFit: 'contain',
                                          borderRadius: '4px',
                                          float: 'right',
                                          ml: 2,
                                          mb: 1,
                                          maxHeight: 300,
                                          display: 'inline-block'
                                       }}
                                    />
                                 )}
                                 <Typography
                                    variant="body1"
                                    component="span"
                                    sx={{
                                       color: theme.palette.text.secondary,
                                       wordBreak: 'break-word',
                                       textAlign: 'justify',
                                       overflow: 'hidden',
                                       display: 'inline',
                                       fontSize: { xs: '0.875rem', sm: '0.925rem', md: '1rem' },
                                    }}
                                 >
                                    {award.overview}
                                    <Typography component="span" variant="h5" sx={{ 
                                       display: 'block', color: theme.palette.text.primary, 
                                       mt: 1, 
                                       fontSize: { xs: '0.925rem', sm: '1rem', md: '1.2rem' } 
                                    }}>
                                       &nbsp;&nbsp;&nbsp;Program Outcome:<br />
                                    </Typography>
                                    {award.outcome}
                                 </Typography>
                              </Box>
                           </Box>
                        )) : (
                           <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.875rem', sm: '0.925rem', md: '1rem' } }}>
                              No awards to display.
                           </Typography>
                        )}
                     </Grid>
                  </Grid>
               </Paper>
            </TimelineContent>
         </TimelineItem>
      );
   }

   return (
      <Container
         component="div"
         sx={{
            width: '100%', 
            padding: 2, 
            boxSizing: 'border-box',
         }}
      >
         <SectionTitle title="Education" />
         <Timeline
            // making the timeline align to the left
            sx={{
               [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0,
               },
            }}
         >
            {schoolDescription(schoolDetails['UCSD'])}
            {schoolDescription(schoolDetails['OCC'])}
         </Timeline>
      </Container>
   );
}

export default Education;